import type { OrganizationPublic, PublicEvent } from '@irace/shared';
import { COPY } from '../copy/vi.ts';
import { formatEventSchedule } from '../lib/format.ts';

/**
 * What the organizer published about the event, below the check-in button.
 *
 * Reference material, not part of the task: a participant at the door fills
 * the form and taps CHECK-IN, and everything here is for the one who arrived
 * early, scanned the wrong day's QR, or wants to know who is running this. It
 * is separated by a rule and kept in muted type for exactly that reason, and
 * the only accent colour belongs to the two links.
 *
 * Renders nothing when the organizer published none of it. An empty framed
 * block below the button would read as a broken page, the same reasoning
 * `EventBanner` uses for a missing banner.
 *
 * Placing organizer links on the form at all was once ruled out, on the
 * grounds that a link out of the page mid-queue costs check-ins. Both links
 * here open in a new tab for that reason: the check-in page, and the
 * submission its IndexedDB may be holding, stay exactly where they were.
 */
export function EventInfoPanel({
  event,
  organization,
  showSchedule = true,
}: {
  event: PublicEvent;
  organization?: OrganizationPublic | null;
  /**
   * False on the "check-in has not opened" screen, which already carries the
   * start time in its own pill. Two lines about the same clock, a thumb apart,
   * read as a mistake rather than as detail.
   */
  showSchedule?: boolean;
}) {
  const schedule = showSchedule
    ? formatEventSchedule(event.starts_at, event.ends_at, event.timezone)
    : null;
  const eventUrl = event.event_url;

  if (!schedule && !organization && !eventUrl) return null;

  return (
    <div className="flex flex-col gap-4 border-t border-border pt-5 text-left">
      {schedule ? (
        <div className="flex flex-col gap-1">
          <Label>{COPY.eventInfo.scheduleLabel}</Label>
          <span className="text-sm font-semibold">{schedule}</span>
        </div>
      ) : null}

      {organization ? (
        <div className="flex flex-col gap-2">
          <Label>{COPY.eventInfo.organizerHeading}</Label>
          <div className="flex items-center gap-3">
            {organization.logo_url ? (
              // Bounded both ways: an organizer's upload is whatever aspect
              // ratio they had, and neither a tall nor a wide one may push the
              // name off its own line.
              <img
                src={organization.logo_url}
                alt=""
                loading="lazy"
                className="max-h-10 w-auto max-w-35 object-contain"
              />
            ) : null}
            <span className="text-sm font-bold">{organization.name}</span>
          </div>
          {organization.description ? (
            // The organizer typed this into a textarea; their line breaks are
            // part of what they wrote.
            <p className="text-sm leading-relaxed whitespace-pre-line text-text-muted">
              {organization.description}
            </p>
          ) : null}
          {organization.website_url ? (
            <ExternalLink href={organization.website_url}>
              {organization.website_url.replace(/^https:\/\//, '').replace(/\/$/, '')}
            </ExternalLink>
          ) : null}
        </div>
      ) : null}

      {eventUrl ? (
        <ExternalLink href={eventUrl}>
          {COPY.eventInfo.eventPageLink}
          <span aria-hidden="true">→</span>
        </ExternalLink>
      ) : null}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-semibold tracking-wide text-text-muted uppercase">
      {children}
    </span>
  );
}

/**
 * A new tab, deliberately: a participant reading this may have a submission
 * waiting in this page's IndexedDB, and navigating the tab away would drop
 * them out of it. `min-h-11` keeps the 44px target §21 asks for, which a bare
 * line of 14px text does not have.
 */
function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      // `gap-1` rather than a space in the label: whitespace between flex
      // children is collapsed, so the arrow would sit against the last word.
      className="inline-flex min-h-11 items-center gap-1 self-start text-sm font-semibold text-brand underline"
    >
      {children}
    </a>
  );
}
